import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { 
  Clapperboard, Users, Video, GraduationCap, Calendar, 
  Search, MapPin, Crown, Clock, ArrowRight, Plus
} from "lucide-react";
import WhatsAppSupport from "../components/WhatsAppSupport";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONTENT_TYPES = [
  { id: "all", label: "All", icon: Clapperboard },
  { id: "casting_call", label: "Casting Calls", icon: Clapperboard },
  { id: "audition", label: "Auditions", icon: Users },
  { id: "meeting", label: "Meetings", icon: Video },
  { id: "live_class", label: "Live Classes", icon: GraduationCap },
];

export default function Services() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [user, setUser] = useState(null);

  useEffect(() => {
    fetchContent();
    checkUser();
  }, [activeTab]);

  const checkUser = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      }
    } catch (err) {
      console.error("Error checking user:", err);
    }
  };

  const fetchContent = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeTab !== "all") {
        params.append("content_type", activeTab);
      }
      
      const res = await fetch(`${API_URL}/api/content/public?${params}`);
      if (res.ok) {
        const data = await res.json();
        setContent(data.content || []);
      }
    } catch (err) {
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  const filteredContent = content.filter(item => 
    item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.creator_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getContentIcon = (type) => {
    switch (type) {
      case "casting_call": return <Clapperboard className="w-5 h-5 text-purple-500" />;
      case "audition": return <Users className="w-5 h-5 text-blue-500" />;
      case "meeting": return <Video className="w-5 h-5 text-green-500" />;
      case "live_class": return <GraduationCap className="w-5 h-5 text-orange-500" />;
      default: return <Clapperboard className="w-5 h-5 text-gray-500" />;
    }
  };

  const isPlatinum = user?.membership?.plan === "platinum" || user?.membership?.plan === "promoter";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-violet-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-600 text-white py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-bold mb-2">Opportunities & Services</h1>
          <p className="text-violet-200">Discover casting calls, auditions, live classes, and networking meetings</p>
          
          {/* Search */}
          <div className="mt-6 max-w-xl">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                placeholder="Search opportunities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white/90 border-0 text-gray-900"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Platinum CTA */}
      {!isPlatinum && (
        <div className="max-w-6xl mx-auto px-4 -mt-6">
          <Card className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Crown className="w-8 h-8" />
                <div>
                  <h3 className="font-semibold">Want to post your own opportunities?</h3>
                  <p className="text-sm text-purple-200">Upgrade to Platinum to create casting calls, auditions & more</p>
                </div>
              </div>
              <Button variant="secondary" onClick={() => navigate("/membership")}>
                Upgrade Now <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 mt-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-5 mb-6">
            {CONTENT_TYPES.map(type => (
              <TabsTrigger key={type.id} value={type.id} className="flex items-center gap-2">
                <type.icon className="w-4 h-4 hidden sm:block" />
                <span className="text-xs sm:text-sm">{type.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {CONTENT_TYPES.map(type => (
            <TabsContent key={type.id} value={type.id}>
              {loading ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3].map(i => (
                    <Card key={i} className="animate-pulse">
                      <CardContent className="p-6">
                        <div className="h-6 bg-gray-200 rounded w-3/4 mb-4" />
                        <div className="h-4 bg-gray-200 rounded w-1/2 mb-2" />
                        <div className="h-4 bg-gray-200 rounded w-2/3" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : filteredContent.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-12">
                    <Clapperboard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-700 mb-2">No opportunities found</h3>
                    <p className="text-gray-500 mb-6">Check back later for new listings</p>
                    {isPlatinum && (
                      <Button onClick={() => navigate("/talent/dashboard")}>
                        <Plus className="w-4 h-4 mr-2" /> Create New Listing
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredContent.map(item => (
                    <Card key={item.content_id} className="hover:shadow-lg transition-shadow cursor-pointer" data-testid={`content-${item.content_id}`}>
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          {getContentIcon(item.content_type)}
                          <Badge variant="outline" className="text-xs">
                            {item.content_type?.replace("_", " ")}
                          </Badge>
                        </div>
                        <CardTitle className="text-lg mt-2">{item.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-gray-600 mb-4 line-clamp-2">{item.description}</p>
                        
                        <div className="space-y-2 text-sm text-gray-500">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            <span>By {item.creator_name}</span>
                          </div>
                          {item.scheduled_at && (
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span>{new Date(item.scheduled_at).toLocaleDateString()}</span>
                            </div>
                          )}
                          {item.max_participants && (
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              <span>Max {item.max_participants} participants</span>
                            </div>
                          )}
                        </div>

                        {item.price && item.price > 0 && (
                          <div className="mt-4 pt-3 border-t">
                            <span className="text-lg font-bold text-violet-600">₹{item.price}</span>
                          </div>
                        )}

                        <Button className="w-full mt-4" onClick={() => {
                          if (!user) {
                            toast.info("Please login to apply");
                            navigate("/login");
                          } else {
                            toast.success("Applied successfully!");
                          }
                        }}>
                          Apply / Join
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      <WhatsAppSupport />
    </div>
  );
}
